<template>

    <div>
        <template v-if="tool && step === 3">
            <div class="row mt-5">
                <div class="col-8 offset-2">
                    <div class="form-group mb-0">

                        <b-form-input
                            id="tool-long-name"
                            v-model="tool.long_name"
                            :state="errors.long_name === null"
                            @input="errors.long_name = null"
                            required
                            :placeholder="$t('Full Tool Name') + ' (*)'"
                            maxlength="25"
                        ></b-form-input>
                    </div>

                    <b-alert :show="errors.long_name !== null" variant="danger">{{ errors.long_name }}</b-alert>

                    <p class="info mt-0 pt-0 pl-3">
                        {{ $t('This will appear as the default headline in your report for the tool (can be changed manually after adding the marking)') }}.
                        <br/> {{ $t('Max characters', {characters: 25}) }}.
                    </p>

                    <div class="form-group mb-0">

                        <b-form-input
                            id="tool-short-name"
                            v-model="tool.short_name"
                            :state="errors.short_name === null"
                            @input="errors.short_name = null"
                            required
                            :placeholder="$t('Short Tool Name') + '(' + $t('shows on tablet / mobile') + ')' + ' (*)'"
                            maxlength="5"
                        ></b-form-input>
                    </div>

                    <b-alert :show="errors.short_name !== null" variant="danger" v-if="errors.short_name !== null">{{ errors.short_name }}</b-alert>

                    <p class="info mt-0 pt-0 pl-3">
                        {{ $t('This name appears in application, and helps to recognise between design markings') }}.
                        <br/> {{ $t('Max characters', {characters: 5}) }}.
                    </p>


                    <div class="form-group mb-0">
                            <textarea v-model="tool.description"
                                      :placeholder="$t('Description') + ' (' + $t('optional') +')'"
                                      class="form-control"
                                      maxlength="255">
                            </textarea>
                    </div>
                    <p class="info mt-0 pt-0 pl-3">
                        {{ $t("This tool description shows in tool's infobox, when descriptions is requested") }}.
                        <br/> {{ $t('Max characters', {characters: 255}) }}.
                    </p>
                </div>
            </div>
        </template>

        <template v-if="tool && step === 4">
            <div class="col-8 offset-2">
                <h6>1. {{ $t("Select Icon") }}*</h6>

                    <div class="row toolTypes">
                        <div :class="tool.icon === 'multiline' ? 'selected' : ''"
                             class="col-4 text-center toolType"
                             @click="changeToolType('multiline')">
                            <div class="toolIcon multiline mb-3"></div>
                            <h4>{{ $t("Multiline") }}</h4>
                            <p class="info" v-html="$t('You may use this tool to draw multiple lines')"></p>
                        </div>

                        <div :class="tool.icon === 'arrow' ? 'selected' : ''"
                             class="col-4 text-center toolType"
                             @click="changeToolType('arrow')">
                            <div class="toolIcon arrow mb-3"></div>
                            <h4>{{ $t("Arrow") }}</h4>
                            <p class="info" v-html="$t('You may use this tool to point something out')"></p>
                        </div>

                        <div :class="tool.icon === 'area' ? 'selected' : ''"
                             class="col-4 text-center toolType"
                             @click="changeToolType('area')">

                            <div
                                style="border: dashed 2px #000; width: 50px; height: 50px; display: inline-block; "></div>

                            <h4>{{ $t("Area") }}</h4>
                            <p class="info" v-html="$t('You may use this tool for selecting areas')"></p>
                        </div>

                    </div>

                    <b-alert :show="errors.icon !== null" variant="danger" v-if="errors.icon !== null">{{ errors.icon }}</b-alert>

                    <template v-if="tool.icon && ['arrow', 'multiline', 'area'].includes(tool.icon)">
                        <hr/>

                        <h6>2. {{ $t("Add detail into icon") }}*</h6>

                        <div class="row">
                            <div class="col-3 text-center toolType">
                                <Icon
                                    :bottom-text="tool.show_lower_detail ? tool.short_name : ''"
                                    :color="tool.color"
                                    :middle-text="tool.show_running_number ? '1' : ''"
                                    :tool-icon="tool.icon"
                                    :tool-type="tool.type"
                                    :top-text="tool.show_upper_detail ? (tool.text_1 ? tool.text_1 : 'DET1') : ''"
                                    class="mb-3"
                                ></Icon>
                            </div>
                            <div class="col-9">
                                <div :class="tool.show_upper_detail ? 'checked' : ''" class="check-box"
                                     @click="tool.show_upper_detail = !tool.show_upper_detail; tool.text_1 = $t(tool.icon);">
                                    <b-form inline>
                                        <label>{{ $t("Show tool type") }}</label>
                                        <b-form-input
                                            v-if="tool.show_upper_detail"
                                            v-model="tool.text_1"
                                            :placeholder="$t('Detail') + ' (*)'" class="ml-3"
                                            maxlength="9"
                                            required
                                            type="text"
                                            @click.stop.prevent=""
                                        ></b-form-input>
                                    </b-form>
                                </div>


                                <div :class="tool.show_running_number ? 'checked' : ''" class="check-box"
                                     @click="tool.show_running_number = !tool.show_running_number">
                                    {{ $t("Show running number") }}
                                </div>

                                <div :class="tool.show_lower_detail ? 'checked' : ''" class="check-box"
                                     @click="tool.show_lower_detail = !tool.show_lower_detail">
                                    {{ $t("Show short name") }}

                                </div>
                            </div>
                        </div>


                        <h6 class="mb-0">3. {{ $t("Select color") }}*</h6>
                        <p class="info">{{ $t('Icon colors can be changed independently on each marking')}}.</p>

                        <div class="color-selector">
                            <div v-for="color in colors" :class="tool.color === color ? 'selected' : ''" class="color"
                                 @click="tool.color = color;">
                                <span :style="'background-color:' + color"></span>
                            </div>
                        </div>

                    </template>
            </div>

        </template>

        <template v-if="tool && step === 5">
            <div class="row mt-5">
                <div class="col-8 offset-2">
                    <h6 :class="tool.has_images ? 'checked' : ''" class="statusTitle"
                        @click="tool.has_images = !tool.has_images">
                        5.1 {{ $t("Add photo or album picture") }}
                        <b-icon icon="caret-down-fill"></b-icon>
                    </h6>
                    <p class="info"
                       v-text="$t('This feature allows you to add picture for you marking from min to max pcs', {min: 1, max: 6})"></p>

                    <template v-if="tool.has_images">
                        <div class="text-center">
                            <b-icon class="rounded-circle bg-pink p-3" font-scale="5" icon="camera"></b-icon>
                            <b-icon class="rounded-circle bg-pink p-3 ml-5" font-scale="5" icon="image"></b-icon>
                        </div>
                    </template>

                    <hr/>

                    <h6 :class="hasCatalog ? 'checked' : ''" class="statusTitle"
                        @click="toggleCatalogInputs">
                        5.2 {{ $t("Add dropdown box") }}
                        <b-icon icon="caret-down-fill"></b-icon>
                    </h6>

                    <p class="info">
                        {{ $t('This feature allows you to add dropdown selection box. You can have a maximum of x dropdowns and text boxes for a tool.', {max: 10}) }}
                        .<br/>
                        {{ $t('Max x selections on one dropdown', {max: 5}) }}
                    </p>

                    <div v-if="hasCatalog" v-for="index in 10" :key="index" class="form-group">
                        <template v-if="index > 1 && tool['catalog_' + index] !== null">
                            <div class="form-group mb-3">
                                <b-form-input
                                    v-model="tool['catalog_' + index]"
                                    :state="errors['catalog_' + index] === null"
                                    @input="errors['catalog_' + index] = null"
                                    required
                                    :placeholder="$t('Dropdown headline') + ' (*)'"
                                ></b-form-input>
                            </div>

                            <b-alert :show="errors['catalog_' + index] !== null" variant="danger" v-if="errors['catalog_' + index] !== null">{{ errors['catalog_' + index] }}</b-alert>

                            <div v-for="(value, valueIndex) in tool['catalog_' + index + '_values']"
                                 v-if="tool['catalog_' + index + '_values']"
                                 class="form-group mb-0">


                                <b-form-input
                                    :value="value.text"
                                    :state="errors['catalog_' + index + '_values'] === null"
                                    @input="errors['catalog_' + index + '_values'] = null"
                                    required
                                    :placeholder="$t('Add selection text') + ' (*)'"
                                    @change="changeCatalogValue(index, valueIndex, $event)"
                                ></b-form-input>

                                <button v-if="valueIndex !== 0" class="remove"
                                        @click="removeCatalogValue(index, valueIndex)"></button>
                            </div>

                            <b-alert :show="errors['catalog_' + index + '_values'] !== null" variant="danger" v-if="errors['catalog_' + index + '_values'] !== null">{{ errors['catalog_' + index + '_values'] }}</b-alert>

                            <div class="mt-3 row">
                                <div class="col-6">
                                    <a v-if="tool['catalog_' + index + '_values'] && tool['catalog_' + index + '_values'].length < maxSelectOptions"
                                       class="underline mt-3"
                                       href="javascript:void(0)" @click="tool['catalog_' + index + '_values'].push('')">
                                        <b-icon icon="plus"></b-icon>
                                        {{ $t("Add another selection box") }}
                                    </a>
                                </div>
                                <div class="col-6">
                                    <a class="underline mt-3 ml-3"
                                       href="javascript:void(0)"
                                       @click="confirmRemoveCatalog(index)">
                                        <b-icon icon="x-circle"></b-icon>
                                        {{ $t("Remove selection box") }}
                                    </a>
                                </div>
                            </div>
                        </template>
                    </div>

                    <hr/>

                    <a v-if="hasEmptyCatalog && hasCatalog && customInputCount < 10" class="underline mt-3"
                       href="javascript:void(0)" @click="addEmptyCatalog()">
                        <b-icon icon="plus"></b-icon>
                        {{ $t("Add another dropdown box") }}
                    </a>

                    <p v-if="hasCatalog && customInputCount >= 10" class="info">
                        {{ $t('You have reached the maximum number of custom inputs (selection and text boxes)') }}
                    </p>

                </div>
            </div>
        </template>

        <template v-if="tool && step === 6">
            <div class="row mt-5">
                <div class="col-8 offset-2">
                    <h6 :class="hasTextBox ? 'checked' : ''" class="statusTitle"
                        @click="toggleTextBoxInputs">
                        6.1 {{ $t("Add text box") }}
                        <b-icon icon="caret-down-fill"></b-icon>
                    </h6>

                    <div v-if="hasTextBox" v-for="index in 10" :key="index" class="form-group">
                        <template v-if="index > 1 && tool['text_' + index] !== null">
                            <div class="form-group mb-3">

                                <b-form-input
                                    v-model="tool['text_' + index]"
                                    :state="errors['text_' + index] === null"
                                    @input="errors['text_' + index] = null"
                                    required
                                    :placeholder="$t('Text box headline') + ' (*)'"
                                ></b-form-input>

                                <button class="remove"
                                        @click="disableTextBox(index)"></button>

                                <b-alert :show="errors['text_' + index] !== null" variant="danger" v-if="errors['text_' + index] !== null">{{ errors['text_' + index] }}</b-alert>
                            </div>


                        </template>
                    </div>

                    <hr/>
                    <a v-if="hasEmptyTextBox && hasTextBox && customInputCount < 10" class="underline mt-3"
                       href="javascript:void(0)" @click="addEmptyTextBox()">
                        <b-icon icon="plus"></b-icon>
                        {{ $t("Add another text box") }}
                    </a>
                    <p v-if="hasTextBox && customInputCount >= 10" class="info">
                        {{ $t('You have reached the maximum number of custom inputs (selection and text boxes)') }}
                    </p>


                </div>
            </div>
        </template>

        <template v-if="tool && step === 7">
            <div class="row mt-3">
                <div class="col-8 offset-2 pb-3">
                    <h6 :class="tool.catalog_1 ? 'checked' : ''" class="statusTitle"
                        @click="toggleCatalog(1, $t('Requires approval'), defaultApprovals)">
                        7.1 {{ $t("Requires approval") }}
                        <b-icon icon="caret-down-fill"></b-icon>
                    </h6>

                    <div class="approvals">
                        <p class="info"
                           v-html="$t('This is a function where site requests approval or comment on this marking or the marking requires approval.')"></p>
                        <div v-for="(catalogValue, index) in tool.catalog_1_values" v-if="tool.catalog_1"
                             class="row mb-3" @key="index">
                            <div class="col-1 offset-2">
                                <div :style="{backgroundColor: catalogValue.value}" class="color"></div>
                            </div>
                            <div class="col-9">

                                <b-form-input
                                    v-model="catalogValue.text"
                                    :state="errors['catalog_1_values'] === null"
                                    @input="errors['catalog_1_values'] = null"
                                    required
                                    :placeholder="$t('Approval state text') + ' (*)'"
                                    class="approval"
                                ></b-form-input>

                                <b-alert :show="errors['catalog_1_values'] !== null" variant="danger" v-if="errors['catalog_1_values'] !== null">{{ errors['catalog_1_values'] }}</b-alert>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <template v-if="tool && step === 8">
            <div class="row mt-3">
                <div class="col-8 offset-2 preview pb-3">

                    <div class="row mb-4">
                        <div class="offset-4 col-3 toolIcon text-center toolType">
                            <Icon
                                :bottom-text="tool.show_lower_detail ? tool.short_name : ''"
                                :color="catalogValues.catalog_1 ? catalogValues.catalog_1.value : tool.color"
                                :middle-text="tool.show_running_number ? '1' : ''"
                                :tool-icon="tool.icon"
                                :tool-type="tool.type"
                                :top-text="tool.show_upper_detail ? (tool.text_1 ? tool.text_1 : '') : ''"
                            ></Icon>
                        </div>
                    </div>

                    <div class="form-group">

                        <label for="toolHeadline">Headline</label>
                        <b-form-input id="toolHeadline" :placeholder="$t('Write tool headline here')"
                                      class="form-control"
                                      maxlength="255" name="tool-headline"
                                      rows="5"></b-form-input>
                    </div>

                    <div v-for="index in 10"
                         v-if="hasCatalog && index > 1 && tool['catalog_' + index] !== null && tool['catalog_' + index + '_values'].length > 0"
                         :key="index + '_catalog'"
                         class="form-group">

                        <label :for="'toolCatalog_' + index">{{ tool['catalog_' + index] }}</label>

                        <b-form-select v-model="catalogValues['catalog_' + index]"
                                       value="null">
                            <b-form-select-option :value="null"
                                                  v-text=""></b-form-select-option>
                            <b-form-select-option v-for="catalogValue in tool['catalog_' + index + '_values']"
                                                  :key="catalogValue.value" :value="catalogValue.value"
                                                  v-text="catalogValue.text">
                            </b-form-select-option>
                        </b-form-select>
                    </div>

                    <div v-for="index in 10" v-if="hasTextBox && index > 1 && tool['text_' + index] !== null" :key="index + '_text'"
                         class="form-group">
                        <label :for="'toolText_' + index">{{ tool['text_' + index] }}</label>

                        <input v-model="textBoxValues['text_' + index]" :placeholder="tool['text_' + index]"
                               class="form-control" maxlength="255"
                               name="short_name"
                               required
                               type="text"/>
                    </div>


                    <div class="form-group">
                        <label for="toolDescription">Description</label>
                        <textarea id="toolDescription" :placeholder="$t('Write description here')" class="form-control"
                                  maxlength="255" name="toolDescription" rows="5"></textarea>
                    </div>

                    <div v-if="tool.catalog_1" class="approvals">
                        <div v-for="(catalogValue, index) in tool.catalog_1_values" v-bind:key="index"
                             class="row mb-3 cursor-pointer" @click="catalogValues.catalog_1 = catalogValue">
                            <div class="col-1 offset-2">
                                <div
                                    :style="{borderStyle: 'solid ', borderColor: catalogValue.value, borderWidth : '2px'}"
                                    class="color">
                                    <svg v-if="catalogValues.catalog_1 === catalogValue" class="icon-img disabled"
                                         height="100%"
                                         width="100%">
                                        <use xlink:href="#check-mark"></use>
                                    </svg>
                                </div>
                            </div>
                            <div class="col-9" v-text="catalogValue.text"></div>
                        </div>
                    </div>

                    <p v-if="tool.has_images" class="info text-center mt-5"
                       v-html="$t('Camera and gallery functions as a default')"></p>


                </div>
            </div>
        </template>

    </div>

</template>


<script>

import FirestopMarkingIcon from "@mobile/components/global/Markings/FirestopMarkingIcon";
import Icon from "./Icon";

export default {
    name: "CustomTool",
    components: {Icon, FirestopMarkingIcon},
    props: {
        tool: {
            type: Object,
            required: false,
            default: null
        },
        step: {
            type: Number,
            required: true,
            default: null
        },
        locale: {
            type: String,
            required: true,
            default: 'en'
        },


    },
    data() {
        return {
            colors: ['#312782', '#BD1622', '#009540', '#E84E1B', '#1D1D1B', '#E5007E'],
            catalogValues: {
                catalog_1: null,
                catalog_2: null,
                catalog_3: null,
                catalog_4: null,
                catalog_5: null,
                catalog_6: null,
                catalog_7: null,
                catalog_8: null,
                catalog_9: null,
                catalog_10: null,
                catalog_11: null,
                catalog_12: null,
                catalog_13: null,
                catalog_14: null,
                catalog_15: null,
                catalog_16: null,
                catalog_17: null,
                catalog_18: null,
                catalog_19: null,
                catalog_20: null,
            },

            textBoxValues: {
                text_1: null,
                text_2: null,
                text_3: null,
                text_4: null,
                text_5: null,
                text_6: null,
                text_7: null,
                text_8: null,
                text_9: null,
                text_10: null,
                text_11: null,
                text_12: null,
                text_13: null,
                text_14: null,
                text_15: null,
                text_16: null,
                text_17: null,
                text_18: null,
                text_19: null,
                text_20: null,
            },
            maxSelectOptions: 30,

            errors: {
                long_name: null,
                short_name: null,
                icon: null,
                catalog_1: null,
                catalog_2: null,
                catalog_3: null,
                catalog_4: null,
                catalog_5: null,
                catalog_6: null,
                catalog_7: null,
                catalog_8: null,
                catalog_9: null,
                catalog_10: null,
                catalog_11: null,
                catalog_12: null,
                catalog_13: null,
                catalog_14: null,
                catalog_15: null,
                catalog_16: null,
                catalog_17: null,
                catalog_18: null,
                catalog_19: null,
                catalog_20: null,

                catalog_1_values: null,
                catalog_2_values: null,
                catalog_3_values: null,
                catalog_4_values: null,
                catalog_5_values: null,
                catalog_6_values: null,
                catalog_7_values: null,
                catalog_8_values: null,
                catalog_9_values: null,
                catalog_10_values: null,
                catalog_11_values: null,
                catalog_12_values: null,
                catalog_13_values: null,
                catalog_14_values: null,
                catalog_15_values: null,
                catalog_16_values: null,
                catalog_17_values: null,
                catalog_18_values: null,
                catalog_19_values: null,
                catalog_20_values: null,

                text_1: null,
                text_2: null,
                text_3: null,
                text_4: null,
                text_5: null,
                text_6: null,
                text_7: null,
                text_8: null,
                text_9: null,
                text_10: null,
                text_11: null,
                text_12: null,
                text_13: null,
                text_14: null,
                text_15: null,
                text_16: null,
                text_17: null,
                text_18: null,
                text_19: null,
                text_20: null,

            }
        };
    },
    mounted() {
        if (!this.tool.id) {
            this.tool.catalog_2 = null;
        }
    },
    methods: {
        toggleCatalogInputs(){
            if(this.tool.has_catalogs){
                this.tool.has_catalogs = false;
            }
            else{
                this.tool.has_catalogs = true;
                if(!this.tool.catalog_2){
                    this.enableCatalog(2);
                }
            }
        },
        toggleTextBoxInputs(){
            if(this.tool.has_text_boxes){
                this.tool.has_text_boxes = false;
            }
            else{
                this.tool.has_text_boxes = true;
                if(!this.tool.text_2){
                    this.tool.text_2 = '';
                }
            }
        },
        toggleCatalog(catalogNr, title, values) {
            if (this.tool['catalog_' + catalogNr]) {
                this.disableCatalog(catalogNr);
            } else {
                this.tool['catalog_' + catalogNr] = title;

                if (values) {
                    this.tool['catalog_' + catalogNr + '_values'] = values;
                } else {
                    this.tool['catalog_' + catalogNr + '_values'] = [];
                }
            }
        },

        emptyCatalogs() {
            for (let i = 2; i <= 10; i++) {
                this.disableCatalog(i);
            }
        },
        enableCatalog(catalogNr) {
            this.tool['catalog_' + catalogNr] = '';
            this.tool['catalog_' + catalogNr + '_values'] = [{text: '', value: ''}];
        },
        disableCatalog(catalogNr) {
            this.tool['catalog_' + catalogNr] = null;
            this.tool['catalog_' + catalogNr + '_values'] = [];
        },
        confirmRemoveCatalog(catalogNr) {
            this.$bvModal.msgBoxConfirm('Are you sure you want to delete this select box?', {
                title: 'Confirmation',
                size: 'md',
                buttonSize: 'md',
                okVariant: 'danger',
                okTitle: 'Delete',
                cancelTitle: 'Cancel',
                hideHeaderClose: false,
                centered: true
            }).then(value => {
                if (value) {
                    this.disableCatalog(catalogNr);
                }
            });

        },
        addEmptyCatalog() {
            for (let i = 2; i <= 10; i++) {
                if (this.tool['catalog_' + i] === null) {
                    this.enableCatalog(i);
                    return false;
                }
            }
        },
        emptyTextBoxes() {
            for (let i = 2; i <= 10; i++) {
                this.disableTextBox(i);
            }
        },
        addEmptyTextBox() {
            for (let i = 2; i <= 10; i++) {
                if (this.tool['text_' + i] === null) {
                    this.enableTextBox(i);
                    return false;
                }
            }
        },
        enableTextBox(textBoxNr) {
            this.tool['text_' + textBoxNr] = '';
        },

        disableTextBox(textBoxNr) {
            this.tool['text_' + textBoxNr] = null;
        },
        changeCatalogValue(catalogNr, key, value) {

            console.log({
                catalogNr: catalogNr,
                key: key,
                event: value
            })

            this.tool['catalog_' + catalogNr + '_values'][key] = {
                text: value,
                value: value
            };
        },

        changeToolType(type) {
            this.tool.icon = type;
            this.tool.text_1 = this.$t(type);

            if(type){
                this.errors.icon = null;
            }
        },

        removeCatalogValue(catalogIndex, valueIndex){
            console.log('remove catalog value', catalogIndex, valueIndex);
            this.tool['catalog_' + catalogIndex + '_values'].splice(valueIndex, 1);
        }
    },
    computed: {
        hasCatalog() {
            return this.tool.has_catalogs;
        },

        hasEmptyCatalog() {
            return this.tool.catalog_2 === null ||
                this.tool.catalog_3 === null ||
                this.tool.catalog_4 === null ||
                this.tool.catalog_5 === null ||
                this.tool.catalog_6 === null ||
                this.tool.catalog_7 === null ||
                this.tool.catalog_8 === null ||
                this.tool.catalog_9 === null ||
                this.tool.catalog_10 === null ||
                this.tool.catalog_11 === null ||
                this.tool.catalog_12 === null ||
                this.tool.catalog_13 === null ||
                this.tool.catalog_14 === null ||
                this.tool.catalog_15 === null ||
                this.tool.catalog_16 === null ||
                this.tool.catalog_17 === null ||
                this.tool.catalog_18 === null ||
                this.tool.catalog_19 === null ||
                this.tool.catalog_20 === null;
        },

        hasTextBox() {
            return this.tool.has_text_boxes;
        },
        hasEmptyTextBox() {
            return this.tool.text_2 === null ||
                this.tool.text_3 === null ||
                this.tool.text_4 === null ||
                this.tool.text_5 === null ||
                this.tool.text_6 === null ||
                this.tool.text_7 === null ||
                this.tool.text_8 === null ||
                this.tool.text_9 === null ||
                this.tool.text_10 === null ||
                this.tool.text_11 === null ||
                this.tool.text_12 === null ||
                this.tool.text_13 === null ||
                this.tool.text_14 === null ||
                this.tool.text_15 === null ||
                this.tool.text_16 === null ||
                this.tool.text_17 === null ||
                this.tool.text_18 === null ||
                this.tool.text_19 === null ||
                this.tool.text_20 === null;
        },

        customInputCount() {
            let arr = [
                this.tool.catalog_2,
                this.tool.catalog_3,
                this.tool.catalog_4,
                this.tool.catalog_5,
                this.tool.catalog_6,
                this.tool.catalog_7,
                this.tool.catalog_8,
                this.tool.catalog_9,
                this.tool.catalog_10,
                this.tool.catalog_11,
                this.tool.catalog_12,
                this.tool.catalog_13,
                this.tool.catalog_14,
                this.tool.catalog_15,
                this.tool.catalog_16,
                this.tool.catalog_17,
                this.tool.catalog_18,
                this.tool.catalog_19,
                this.tool.catalog_20,
                this.tool.text_2,
                this.tool.text_3,
                this.tool.text_4,
                this.tool.text_5,
                this.tool.text_6,
                this.tool.text_7,
                this.tool.text_8,
                this.tool.text_9,
                this.tool.text_10,
                this.tool.text_11,
                this.tool.text_12,
                this.tool.text_13,
                this.tool.text_14,
                this.tool.text_15,
                this.tool.text_16,
                this.tool.text_17,
                this.tool.text_18,
                this.tool.text_19,
                this.tool.text_20
            ];

            return arr.filter(customInput => {
                return customInput !== null;
            }).length;
        },

        defaultApprovals() {
            return [
                {value: '#A91E23', text: this.$t('Not approved')},
                {value: '#F8B133', text: this.$t('Waiting')},
                {value: '#008D36', text: this.$t('Approved')},
            ];
        },
    },
    watch: {
        step(newStep, oldStep){
            let hasErrors = false;

            if(newStep === 4){
                if(!this.tool.long_name){
                    this.errors.long_name = this.$t('Tool long name is required');
                    hasErrors = true;
                }

                if(!this.tool.short_name){
                    this.errors.short_name = this.$t('Tool short name is required');
                    hasErrors = true;
                }
            }
            else if(newStep === 5){
                if(!this.tool.icon || !['arrow', 'multiline', 'area'].includes(this.tool.icon)){
                    this.errors.icon = this.$t('Select an icon for your tool');
                    hasErrors = true;
                }
            }
            else if(newStep === 6){
                if(this.tool.has_catalogs){
                    for(let i = 2; i <= 10; i++){
                        if(this.tool['catalog_' + i] !== null){
                            if(!this.tool['catalog_' + i]){
                                this.errors['catalog_' + i] = this.$t('Dropdown headline can not be empty');
                                hasErrors = true;
                            }

                            this.tool['catalog_' + i + '_values'].forEach(catalogValue => {
                                if(!catalogValue || (catalogValue.text !== 0 && !catalogValue.text)){
                                    this.errors['catalog_' + i + '_values'] = this.$t('Selection text entry can not be empty');
                                    hasErrors = true;
                                }
                            });
                        }
                    }
                }

            }
            else if(newStep === 7){
                if(this.tool.has_text_boxes){
                    for(let i = 2; i <= 10; i++){

                        if(this.tool['text_' + i] !== null && !this.tool['text_' + i]){
                            this.errors['text_' + i] = this.$t('Text box headline can not be empty');
                            hasErrors = true;
                        }
                    }
                }
            }
            else if(newStep === 8){
                if(this.tool['catalog_1'] !== null){
                    this.tool['catalog_1_values'].forEach(catalogValue => {
                        if(!catalogValue || (catalogValue.text !== 0 && !catalogValue.text)){
                            this.errors['catalog_1_values'] = this.$t('Approval text entry can not be empty');
                            hasErrors = true;
                        }
                    });
                }
            }

            if(hasErrors){
                this.$emit('jumpToStep', oldStep);
            }
        },
    }

}
</script>

<style scoped>
label {
    padding: 0;
    margin: 0;
}
</style>
